/**
 * DD entry — the single definition of "this deal is in due diligence".
 *
 * THE MODEL
 * ---------
 * The STAGE says whether a deal is in due diligence. A deal is on the DD surface
 * at `awaiting_access` (the investor has asked for the data room) and at
 * `diligence` (the founder has said yes) — which is exactly
 * `appearsOn(stage, 'diligence')`, so this module reads the spine rather than
 * keeping its own opinion.
 *
 * THIS HEADER USED TO SAY THE REQUEST WAS THE ENTRY GATE. It is not, and the
 * correction is the whole point of the change that touched this file. An
 * investor cannot start diligence on a company: they REQUEST access, and the
 * FOUNDER'S AUTHORISATION is the single event that both opens the documents and
 * advances the deal (`respond_to_data_room_request`, 20260805140001). Treating
 * the request as entry is what let the client promise a workspace it could not
 * open. A stale comment here is not cosmetic — a "not wired yet" header that was
 * three weeks out of date put a false claim into the PRD earlier this week.
 *
 * Whether the documents are READABLE is a separate question from whether the
 * deal is in DD, and `DDAccessState` below is what describes it: a deal sits on
 * this surface from the moment access is requested, with the documents shut
 * until the founder answers. The DD detail screen renders a locked state for it.
 * That question is answered by the stage too — `dataRoomOpen(status)` — since
 * 20260805160000 retired `dealflow.data_room_access`, the second store that
 * used to answer it.
 *
 * WHY IT IS CENTRALISED HERE
 * --------------------------
 * The dashboard and the DD screen each had their own, mutually exclusive
 * predicate (`status in (reviewing, interested)` vs `dataRoomAccess`), which is
 * how production ended up advertising "1 due diligence review pending" on a
 * screen that could only ever render zero. Both surfaces now consume
 * `selectDDReviews`, so they cannot disagree.
 *
 * Pure and unit-testable without React Query or supabase-js — same pattern as
 * `supabase/shortlist-dealflow-contract.ts`. Its one import is
 * `lib/dealflow/stage`, itself a zero-import leaf, so nothing is dragged in.
 */

import { appearsOn, dataRoomOpen } from '@/lib/dealflow/stage';
import type { DealflowStartup } from '@/lib/state/dealflow-store';

/**
 * Where the founder has got to on the investor's access request.
 *
 * - `awaiting`  — requested, founder has not responded. In DD, documents locked.
 * - `granted`   — founder approved. Documents readable.
 * - `declined`  — founder refused. The deal is NOT evicted from the investor's
 *                 pipeline (a founder cannot do that to someone else's board);
 *                 the approval RPC returns it to `interested`, which is off the
 *                 DD surface. This state therefore renders only while a rejected
 *                 request and the stage have not yet converged.
 */
export type DDAccessState = 'awaiting' | 'granted' | 'declined';

/** The subset of a `data_room_access_requests` row this module reads. */
export interface DataRoomAccessRequestRow {
  startup_id: string;
  investor_profile_id: string;
  status: string | null;
  created_at: string | null;
  responded_at: string | null;
}

/**
 * What the investor's Request-access button should show for one startup.
 *
 * `none` is "never asked". Everything else means a request row EXISTS, which is
 * the fact that matters: the write is keyed `UNIQUE (startup_id,
 * investor_profile_id)`, so a second request hits ON CONFLICT DO UPDATE, and
 * the investor has no UPDATE policy on the table — only the startup side does.
 * Re-requesting is therefore always refused (INVERIO-APP-P/-Q/-X/-Y).
 */
export type DataRoomRequestState = 'none' | 'pending' | 'approved' | 'rejected';

/**
 * The investor's request state for one startup, read straight from their own
 * request rows.
 *
 * DELIBERATELY STAGE-BLIND, unlike `selectDDReviews` below. That function
 * filters to deals already on the DD surface, which is right for a list of
 * diligences and wrong for a button: a deal at `interested` with a live request
 * row is absent from it, so the button concluded "no state" and offered Request
 * access a second time. That is the whole bug.
 *
 * Unknown and null statuses resolve to `pending` rather than `none`. The safe
 * default is "a row exists" — that is what makes the next write fail, and
 * claiming never-asked would re-offer the button that cannot succeed.
 */
export function requestStateFor(
  startupId: string | undefined,
  requests: readonly DataRoomAccessRequestRow[],
): DataRoomRequestState {
  if (!startupId) return 'none';
  const match = requests.find((r) => r.startup_id === startupId);
  if (!match) return 'none';
  if (match.status === 'approved') return 'approved';
  if (match.status === 'rejected') return 'rejected';
  return 'pending';
}

/** A dealflow deal the investor has explicitly moved into due diligence. */
export interface DDReview {
  deal: DealflowStartup;
  accessState: DDAccessState;
  /** When the investor started DD (i.e. requested access). '' if not loaded. */
  requestedAt: string;
  /** When the founder responded, or null while awaiting. */
  respondedAt: string | null;
}

/** Map a raw request status to an access state. Unknown/null ⇒ still awaiting. */
function toAccessState(status: string | null): DDAccessState {
  if (status === 'approved') return 'granted';
  if (status === 'rejected') return 'declined';
  return 'awaiting';
}

/**
 * Build the due-diligence list for one investor.
 *
 * @param dealflow The investor's pipeline (store contents).
 * @param requests The investor's own `data_room_access_requests` rows.
 *
 * A deal is included when its STAGE puts it on the DD surface — `awaiting_access`
 * or `diligence`, i.e. `appearsOn(stage, 'diligence')`. That is the whole rule.
 *
 * TWO MORE CLAUSES USED TO SIT HERE — "a request row exists" and "`dataRoomAccess`
 * is true" — as grandfathering, because 20260805140000 did not backfill and the
 * stage could therefore disagree with the grant. 20260805160000 backfilled and
 * deleted the boolean, so they cannot disagree any more and there is nothing
 * left to grandfather. Their cost was real: a request row outlives the deal, so
 * the row-based clause kept CONCLUDED work on the DD screen forever (pinned in
 * the tests, which now pin its absence).
 *
 * The consequence to know: a declined request returns the deal to `interested`
 * (the approval RPC does that), which is off this surface — so a refusal drops
 * the deal back to the pipeline rather than lingering here labelled "declined".
 * `DDAccessState` still models `declined` for a request that reads rejected
 * while the stage has not yet caught up.
 *
 * Raising-feed items are excluded: they have no dealflow row, so there is
 * nothing to run DD against until they are added to the pipeline.
 */
export function selectDDReviews(
  dealflow: DealflowStartup[],
  requests: DataRoomAccessRequestRow[],
): DDReview[] {
  // Newest request wins if duplicates ever appear (the DB enforces one row per
  // (startup_id, investor_profile_id), but the UI must not fan a deal into two cards).
  const byStartup = new Map<string, DataRoomAccessRequestRow>();
  for (const row of requests) {
    const existing = byStartup.get(row.startup_id);
    if (!existing || (row.created_at ?? '') >= (existing.created_at ?? '')) {
      byStartup.set(row.startup_id, row);
    }
  }

  const reviews: DDReview[] = [];

  for (const deal of dealflow) {
    // No dealflow row behind it ⇒ nothing to run DD against.
    if (deal.source === 'raising') continue;

    if (!appearsOn(deal.status, 'diligence')) continue;

    const req = byStartup.get(deal.startup.id);

    // The STAGE is the ground truth for readability: `diligence` is written by
    // the same statement pair that writes the grant, so if the deal is there the
    // documents are genuinely reachable and a request row still reading
    // 'pending' is stale bookkeeping that must not lock the UI.
    const accessState: DDAccessState = dataRoomOpen(deal.status)
      ? 'granted'
      : toAccessState(req?.status ?? null);

    reviews.push({
      deal,
      accessState,
      requestedAt: req?.created_at ?? '',
      respondedAt: req?.responded_at ?? null,
    });
  }

  // Most recently started first. A deal whose request row has not loaded (or
  // predates the request table) carries '' and sorts last, which is right — it
  // is the oldest by definition.
  return reviews.sort((a, b) => b.requestedAt.localeCompare(a.requestedAt));
}

/**
 * The count the dashboard shows as "due diligence reviews pending".
 *
 * Deliberately the same list the DD screen renders — the drift between these
 * two numbers was the original bug. A concluded review is not pending, so
 * `concludedStartupIds` (from persisted `dd_reports`) is subtracted.
 */
export function countPendingDDReviews(
  reviews: DDReview[],
  concludedStartupIds: ReadonlySet<string> = new Set(),
): number {
  return reviews.filter((r) => !concludedStartupIds.has(r.deal.startup.id)).length;
}
